import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { submitRegistrationRequest } from '../lib/auth';
import { trackRegistration } from '../lib/analytics';
import RoleSelectionStep from '../components/registration/RoleSelectionStep';
import ClientDetailsStep from '../components/registration/ClientDetailsStep';
import ProviderDetailsStep from '../components/registration/ProviderDetailsStep';
import ContactDetailsStep from '../components/registration/ContactDetailsStep';
import ReviewStep from '../components/registration/ReviewStep';

export interface RegistrationData {
  role: 'CLIENT' | 'FREELANCER' | 'AGENCY';
  // Client specific
  companyName?: string;
  // Provider specific
  providerType?: 'FREELANCER' | 'AGENCY';
  companyBulstat?: string; // For agencies only
  skills?: string[];
  bio?: string; // Changed from experience to bio to match database
  portfolioUrl?: string;
  hourlyRate?: number;
  // Contact details
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  termsAccepted: boolean;
}

const steps = [
  'auth.register.steps.roleSelection',
  'auth.register.steps.roleDetails',
  'auth.register.steps.contactDetails',
  'auth.register.steps.review',
];

const RegisterPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [registrationData, setRegistrationData] = useState<RegistrationData>({
    role: 'CLIENT',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    termsAccepted: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Check if user is already logged in
  useEffect(() => {
    const checkLoginState = () => {
      const loggedIn = localStorage.getItem('isLoggedIn') === 'true';
      setIsLoggedIn(loggedIn);
      
      if (loggedIn) {
        // Redirect to home page if already logged in
        navigate('/');
      }
    };

    checkLoginState();

    // Listen for login state changes
    window.addEventListener('loginSuccess', checkLoginState);
    window.addEventListener('logout', checkLoginState);
    
    return () => {
      window.removeEventListener('loginSuccess', checkLoginState);
      window.removeEventListener('logout', checkLoginState);
    };
  }, [navigate]);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setError('');
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    setError('');
  };

  const handleDataChange = (data: Partial<RegistrationData>) => {
    setRegistrationData(prev => ({ ...prev, ...data }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      const result = await submitRegistrationRequest(registrationData);
      
      if (result.success) {
        // Track successful registration
        trackRegistration('email', registrationData.role.toLowerCase() as 'client' | 'freelancer');
        navigate('/register-success');
      } else {
        setError(result.error || t('auth.register.error'));
      }
    } catch (err) {
      setError(t('auth.register.unexpectedError'));
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <RoleSelectionStep
            data={registrationData}
            onDataChange={handleDataChange}
            onNext={handleNext}
          />
        );
      case 1:
        return registrationData.role === 'CLIENT' ? (
          <ClientDetailsStep
            data={registrationData}
            onDataChange={handleDataChange}
            onBack={handleBack}
            onNext={handleNext}
          />
        ) : (
          <ProviderDetailsStep
            data={registrationData}
            onDataChange={handleDataChange}
            onBack={handleBack}
            onNext={handleNext}
          />
        );
      case 2:
        return (
          <ContactDetailsStep
            data={registrationData}
            onDataChange={handleDataChange}
            onBack={handleBack}
            onNext={handleNext}
          />
        );
      case 3:
        return (
          <ReviewStep
            data={registrationData}
            onBack={handleBack}
            onSubmit={handleSubmit}
            loading={loading}
          />
        );
      default:
        return null;
    }
  };

  // Don't render if already logged in
  if (isLoggedIn) {
    return null;
  }

  return (
    <Box sx={{ backgroundColor: '#0f0f23', minHeight: '100vh' }}>
      <Header />
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 100%)',
          py: 4,
        }}
      >
        <Container maxWidth="md" sx={{ width: '80%' }}>
        <Paper
          elevation={24}
          sx={{
            p: 4,
            borderRadius: 3,
            background: 'rgba(26, 26, 46, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #28A0F0 0%, #ec4899 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1,
              }}
            >
              {t('auth.register.title') || 'Регистрация в Workio'}
            </Typography>
            <Typography variant="body1" sx={{ color: '#a0a0a0' }}>
              {t('auth.register.subtitle') || 'Създайте акаунт и започнете да работите'}
            </Typography>
          </Box>

          {/* Stepper */}
          <Stepper
            activeStep={activeStep}
            sx={{
              mb: 4,
              '& .MuiStepLabel-root .Mui-completed': {
                color: '#28A0F0',
              },
              '& .MuiStepLabel-root .Mui-active': {
                color: '#28A0F0',
              },
              '& .MuiStepLabel-root .MuiStepLabel-label': {
                color: '#a0a0a0',
              },
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{t(label)}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {/* Step Content */}
          <Box sx={{ minHeight: 400 }}>
            {renderStepContent(activeStep)}
          </Box>
        </Paper>
      </Container>
      </Box>
    </Box>
  );
};

export default RegisterPage;
